import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    DataProcessorOfficerAccessResponse,
    DataProcessorOfficerAccessSearchResponse,
    DataProcessorOfficerRequest,
    DataProcessorOfficerUsersSearchResponse,
    UpsertDataProcessorOfficerAccessRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDPO.api.ts';
import {
    createApiDataProcessorOfficerAccess,
    deleteApiDataProcessorOfficerAccess,
    getApiDataProcessorOfficerAccess,
    getApiDataProcessorOfficerUsers,
    updateApiDataProcessorOfficerAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDPO.api.ts';
import { extractError } from '@utils/slice.ts';

interface DataProcessorOfficerShoreError {
    message?: string;
    [key: string]: any;
}

interface DataProcessorOfficerShoreState {
    error: DataProcessorOfficerShoreError | null;
    dataProcessorOfficerShoreData: DataProcessorOfficerAccessSearchResponse | null;
    dataProcessorOfficerShoreUsersData: DataProcessorOfficerUsersSearchResponse | null;

    isGetDataProcessorOfficerShorePending: boolean;
    isCreateDataProcessorOfficerShorePending: boolean;
    isUpdateDataProcessorOfficerShorePending: boolean;
    isDeleteDataProcessorOfficerShorePending: boolean;
    isGetDataProcessorOfficerShoreUsersPending: boolean;
}

const initialState: DataProcessorOfficerShoreState = {
    error: null,
    dataProcessorOfficerShoreData: null,
    dataProcessorOfficerShoreUsersData: null,

    isGetDataProcessorOfficerShorePending: false,
    isCreateDataProcessorOfficerShorePending: false,
    isUpdateDataProcessorOfficerShorePending: false,
    isDeleteDataProcessorOfficerShorePending: false,
    isGetDataProcessorOfficerShoreUsersPending: false,
};

export const getDataProcessorOfficerShore = createAsyncThunk<
    DataProcessorOfficerAccessSearchResponse,
    { tenantAlias: string; payload: DataProcessorOfficerRequest },
    { rejectValue: DataProcessorOfficerShoreError }
>('dataProcessorOfficerShore/getDataProcessorOfficerShore', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataProcessorOfficerAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createDataProcessorOfficerShore = createAsyncThunk<
    DataProcessorOfficerAccessResponse,
    { tenantAlias: string; payload: UpsertDataProcessorOfficerAccessRequest },
    { rejectValue: DataProcessorOfficerShoreError }
>('dataProcessorOfficerShore/createDataProcessorOfficerShore', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiDataProcessorOfficerAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateDataProcessorOfficerShore = createAsyncThunk<
    DataProcessorOfficerAccessResponse,
    { tenantAlias: string; accessId: number; payload: UpsertDataProcessorOfficerAccessRequest },
    { rejectValue: DataProcessorOfficerShoreError }
>(
    'dataProcessorOfficerShore/updateDataProcessorOfficerShore',
    ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
        updateApiDataProcessorOfficerAccess(tenantAlias, accessId, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const deleteDataProcessorOfficerShore = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: DataProcessorOfficerShoreError }
>('dataProcessorOfficerShore/deleteDataProcessorOfficerShore', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiDataProcessorOfficerAccess(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

export const getDataProcessorOfficerShoreUsers = createAsyncThunk<
    DataProcessorOfficerUsersSearchResponse,
    { tenantAlias: string; payload: DataProcessorOfficerRequest },
    { rejectValue: DataProcessorOfficerShoreError }
>('dataProcessorOfficerShore/getDataProcessorOfficerShoreUsers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataProcessorOfficerUsers(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const dataProcessorOfficerShoreSlice = createSlice({
    name: 'dataProcessorOfficerShore',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getDataProcessorOfficerShore.pending, (state) => {
            state.isGetDataProcessorOfficerShorePending = true;
            state.error = null;
        });
        builder.addCase(getDataProcessorOfficerShore.fulfilled, (state, action) => {
            state.isGetDataProcessorOfficerShorePending = false;
            state.dataProcessorOfficerShoreData = action.payload;
        });
        builder.addCase(getDataProcessorOfficerShore.rejected, (state, action) => {
            state.isGetDataProcessorOfficerShorePending = false;
            state.error = extractError(action);
        });

        builder.addCase(createDataProcessorOfficerShore.pending, (state) => {
            state.isCreateDataProcessorOfficerShorePending = true;
            state.error = null;
        });
        builder.addCase(createDataProcessorOfficerShore.fulfilled, (state) => {
            state.isCreateDataProcessorOfficerShorePending = false;
        });
        builder.addCase(createDataProcessorOfficerShore.rejected, (state, action) => {
            state.isCreateDataProcessorOfficerShorePending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateDataProcessorOfficerShore.pending, (state) => {
            state.isUpdateDataProcessorOfficerShorePending = true;
            state.error = null;
        });
        builder.addCase(updateDataProcessorOfficerShore.fulfilled, (state) => {
            state.isUpdateDataProcessorOfficerShorePending = false;
        });
        builder.addCase(updateDataProcessorOfficerShore.rejected, (state, action) => {
            state.isUpdateDataProcessorOfficerShorePending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteDataProcessorOfficerShore.pending, (state) => {
            state.isDeleteDataProcessorOfficerShorePending = true;
            state.error = null;
        });
        builder.addCase(deleteDataProcessorOfficerShore.fulfilled, (state) => {
            state.isDeleteDataProcessorOfficerShorePending = false;
        });
        builder.addCase(deleteDataProcessorOfficerShore.rejected, (state, action) => {
            state.isDeleteDataProcessorOfficerShorePending = false;
            state.error = extractError(action);
        });

        builder.addCase(getDataProcessorOfficerShoreUsers.pending, (state) => {
            state.isGetDataProcessorOfficerShoreUsersPending = true;
            state.error = null;
        });
        builder.addCase(getDataProcessorOfficerShoreUsers.fulfilled, (state, action) => {
            state.isGetDataProcessorOfficerShoreUsersPending = false;
            state.dataProcessorOfficerShoreUsersData = action.payload;
        });
        builder.addCase(getDataProcessorOfficerShoreUsers.rejected, (state, action) => {
            state.isGetDataProcessorOfficerShoreUsersPending = false;
            state.error = extractError(action);
        });
    },
});

export default dataProcessorOfficerShoreSlice.reducer;
